import React, { useEffect, useRef, useState } from "react";
import { colors } from "@styles/colors";
import { Text, YStack, XStack, Button, Sheet, ScrollView, Spinner } from "tamagui";
import { RiCoupon3Line, RiCloseFill } from "react-icons/ri";
import api from "@services/axios/apiManager";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { formatDate } from "@utils/func/timeFormatter";
import { isBeforeUseStart, couponExpiryLabel } from "@utils/func/couponPeriod";
import { applicableItemsLabel, isApplicableItemsSelectableInApp } from "@utils/func/couponScope";
import { triggerCouponLazyIssue } from "@utils/api/couponApi";
import { Coupon } from "@screen/Home/pages/Account/components/coupons/couponData";
import { updateDraftOrder } from "@components/product/API/handleProductAPI";
import { useOrder } from "@utils/store/cart/useOrder";
import { useUser } from "@utils/store/user/useUser";
import toast from "react-hot-toast";
import { buildXContextHeader } from "@utils/api/headers";

interface CouponItem {
  customer_coupon_id: string;
  coupon_name: string;
  coupon_code: string;
  discount_value: number;
  min_order_amount: number;
  applicable_items: string;
  supplier_id: string | null;
  status: "available" | "used" | "expired";
  use_start_time: string | null;
  expire_time_stamp: string;
  used_time_stamp: string | null;
}

export function CouponSwitchMenu({
  themeColor = "white",
  changeColor,
  flush = false,
  amountAccent = false,
  selectId,
  onLoaded,
  onAutoResolved,
}: {
  themeColor?: string;
  changeColor?: string;
  /** 觸發鈕不帶水平內距，讓值與上方區塊標題切齊（瓦斯訂購 Skin V2 用）。 */
  flush?: boolean;
  /** 折抵金額改用綠色徽章呈現（存款語意），比原本的淡色小字明顯。 */
  amountAccent?: boolean;
  selectId?: string;
  /** 回報目前可用優惠券數量（含載入完成後為 0 的情況），讓父層決定是否隱藏整個欄位 */
  onLoaded?: (count: number) => void;
  /** 自動帶券決策完成（已帶券 or 確定無券）後呼叫一次，讓父層放行 preview 算費、避免先閃無券原價 */
  onAutoResolved?: () => void;
}) {
  const activeColor = colors[themeColor as keyof typeof colors] || themeColor;
  const changeTextColor = changeColor ? colors[changeColor as keyof typeof colors] || changeColor : activeColor;
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [loading, setLoading] = useState(false);
  // 券是否已抓取完成（成功或失敗）；用來區分「尚未抓」與「抓完是空的」，避免自動帶券在券回來前就誤判無券
  const [couponsLoaded, setCouponsLoaded] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [open, setOpen] = useState(false);
  const { cusJwtToken } = useCusAuth();
  const { draftOrderId, setCustomerCouponId, syncDraft, cartProductTotalPrice, customerCouponId } = useOrder();
  const { chooseIdentity } = useUser();
  // 確保自動帶券只跑一次（不覆蓋使用者後續手動變更）
  const autoSelectedRef = useRef(false);

  const fetchAvailableCoupons = async () => {
    if (!cusJwtToken) return;
    setLoading(true);
    try {
      // 先觸發後端 lazy 補發限量券（使用平台事件），帶 X-Context 讓後端連該瓦斯行限量券一起補；
      // 再抓清單讓剛補發的券立刻出現
      await triggerCouponLazyIssue(cusJwtToken, buildXContextHeader(chooseIdentity) || "");
      const res = await api.get("/app-api/coupons", {
        params: { status: "available", limit: 100, page: 1 },
        headers: { Authorization: `Bearer ${cusJwtToken}` },
      });

      const items: CouponItem[] = res.data.data.items;
      const mapped: Coupon[] = items.map((item) => ({
        id: item.customer_coupon_id,
        title: item.coupon_name,
        amount: item.discount_value,
        minSpend: item.min_order_amount,
        expiry: item.expire_time_stamp ? formatDate(item.expire_time_stamp) : "",
        scope: applicableItemsLabel(item.applicable_items),
        source: item.supplier_id ? "帳戶券" : "平台券",
        used: false,
        usedDate: null,
        supplierId: item.supplier_id,
        applicableItems: item.applicable_items,
        code: item.coupon_code,
        useStart: item.use_start_time,
      }));

      setCoupons(mapped);
      onLoaded?.(mapped.length);
    } catch (error) {
      setCoupons([]);
      onLoaded?.(0);
      console.error("CouponSwitchMenu fetchCoupons error", error);
    } finally {
      setLoading(false);
      setCouponsLoaded(true);
    }
  };

  useEffect(() => {
    fetchAvailableCoupons();
  }, [cusJwtToken]);

  // 當 selectId 或 coupons 改變時，同步選中的優惠券
  useEffect(() => {
    if (selectId && coupons.length > 0) {
      const target = coupons.find((c) => c.id === selectId);
      if (target) {
        setSelectedCoupon(target);
      } else {
        setSelectedCoupon(null);
      }
    } else if (!selectId) {
      setSelectedCoupon(null);
    }
  }, [selectId, coupons]);

  const handleSelect = async (coupon: Coupon) => {
    const isNewSelection = selectedCoupon?.id !== coupon.id;
    // 未到使用開始時間的券：擋下並提示（後端也會擋，這裡先給即時回饋）
    if (isNewSelection && isBeforeUseStart(coupon.useStart)) {
      toast.error(`此優惠券 ${coupon.useStart ? formatDate(coupon.useStart) : ""} 起才可使用`);
      return;
    }
    // 僅配件券無法用於瓦斯訂單（App 購物車只有瓦斯）
    if (isNewSelection && !isApplicableItemsSelectableInApp(coupon.applicableItems)) {
      toast.error("此優惠券僅限配件使用，無法用於瓦斯訂單");
      return;
    }
    const next = selectedCoupon?.id === coupon.id ? null : coupon;
    // 失敗時用來 rollback 樂觀更新
    const previousSelectedCoupon = selectedCoupon;
    const previousCouponId = useOrder.getState().customerCouponId;

    // 樂觀更新：在 await PUT 前先寫 store → GasOrderSummary 立刻因 customerCouponId 變動 fire preview，
    // PUT 跟 preview 並行跑，比序列省 1 個 round trip 的等待時間
    setSelectedCoupon(next);
    setCustomerCouponId(next?.id ?? null);
    setOpen(false);

    setUpdating(true);
    try {
      const res = await updateDraftOrder({
        draftId: draftOrderId || "",
        body: { customer_coupon_id: next?.id ?? null },
        jwtToken: cusJwtToken || "",
        xContext: buildXContextHeader(chooseIdentity) || "",
      });

      if (res.state === "error") {
        // PUT 失敗 → rollback 樂觀更新；preview 會因 store 再變一次而自動重算
        setSelectedCoupon(previousSelectedCoupon);
        setCustomerCouponId(previousCouponId);
        toast.error("套用優惠券失敗");
      } else {
        // PUT 成功 → sync productList（server 可能更新 line_id 等欄位）
        // deliveryTime 由 syncDraft 保護：draft 沒帶值就不會洗掉本地選的時段
        const updatedDraft = res.data?.data;
        if (updatedDraft) syncDraft(updatedDraft);
      }
    } catch (error) {
      console.error("CouponSwitchMenu updateDraftOrder error", error);
      setSelectedCoupon(previousSelectedCoupon);
      setCustomerCouponId(previousCouponId);
    } finally {
      setUpdating(false);
    }
  };

  // 自動帶入「折最多錢」的可用優惠券（草稿已有券則不覆蓋；只跑一次）
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (!couponsLoaded) return; // 券尚未抓完，先別決策（避免在券回來前誤判無券）
    if (cartProductTotalPrice <= 0) return; // 等購物車金額載入再判斷符合資格的券

    // 到這裡：券已抓完（可能 0 張）、購物車金額已知 → 做一次自動帶券決策
    autoSelectedRef.current = true;
    if (!customerCouponId && coupons.length > 0) {
      const supplierId = chooseIdentity?.primary_supplier?.supplier_id;
      // 符合資格：達低消、已到使用開始時間，且為平台券（supplierId 為空）或對應到本瓦斯行的帳戶券
      const eligible = coupons.filter(
        (c) =>
          (c.minSpend ?? 0) <= cartProductTotalPrice &&
          !isBeforeUseStart(c.useStart) &&
          isApplicableItemsSelectableInApp(c.applicableItems) &&
          (!c.supplierId || c.supplierId === supplierId),
      );
      if (eligible.length > 0) {
        const best = eligible.reduce((a, b) => (b.amount > a.amount ? b : a));
        handleSelect(best);
      }
    }
    // 決策完成（不論有無帶券）通知父層放行 preview 算費
    onAutoResolved?.();
  }, [coupons, couponsLoaded, cartProductTotalPrice, customerCouponId]);

  return (
    <>
      <Button
        backgroundColor="rgba(255, 255, 255, 0.2)"
        p={10}
        px={flush ? 0 : 10}
        py={flush ? 0 : 10}
        // flush（結帳頁卡片內）高度與 AddressSwitchMenu／配送時間列一致，
        // 三張卡的值列才會落在同一個行盒高度上，卡內間距看起來才均勻。
        h={flush ? 21 : 40}
        width="100%"
        hoverStyle={{ backgroundColor: "rgba(255, 255, 255, 0.2)" }}
        pressStyle={{ backgroundColor: "rgba(255, 255, 255, 0.2)", opacity: 0.8 }}
        borderColor="rgba(255,255,255,0.2)"
        borderWidth="$0.2"
        onPress={() => setOpen(true)}>
        <XStack justifyContent="space-between" ai="center" width="100%">
          <XStack ai="center" gap={8} flex={1}>
            {loading || updating ? (
              <Spinner size="small" color={activeColor} />
            ) : selectedCoupon ? (
              <Text col={activeColor} fontSize={14} fontWeight="bold" numberOfLines={1} flex={1} textAlign="left">
                {selectedCoupon.title}
              </Text>
            ) : (
              <Text col={activeColor} fontSize={14} fontWeight="bold" numberOfLines={1} flex={1} textAlign="left">
                未使用優惠券
              </Text>
            )}
          </XStack>
          {/* 已選券：在「變更」左邊顯示此券折抵金額，讓使用者一眼看到折了多少 */}
          {selectedCoupon &&
            !loading &&
            !updating &&
            (amountAccent ? (
              /* 綠色膠囊 + 票券圖示。用綠不用主色藍：折抵是「你少付了」，
                 和旁邊那些藍色的可點元素（變更、連結）意義不同，同色會被讀成另一個按鈕。
                 圖示與費用明細那行的折抵同一顆，兩處講的是同一件事。 */
              <XStack
                backgroundColor={colors.bgGreen}
                borderWidth={1}
                borderColor="rgba(16,185,129,0.35)"
                px={9}
                py={3}
                borderRadius={999}
                ml="$2"
                flexShrink={0}
                ai="center"
                gap={4}>
                <RiCoupon3Line size={13} color={colors.greenText} />
                <Text col={colors.greenText} fontSize={13} fontWeight="800" numberOfLines={1}>
                  折 ${selectedCoupon.amount.toLocaleString()}
                </Text>
              </XStack>
            ) : (
              <Text col={activeColor} fontSize={13} fontWeight="800" ml="$2" numberOfLines={1} style={{ opacity: 0.85 }}>
                折 ${selectedCoupon.amount.toLocaleString()}
              </Text>
            ))}
          <Text color={changeTextColor} fontSize={14} fontWeight="bold" ml="$2">
            變更
          </Text>
        </XStack>
      </Button>

      <Sheet
        disableDrag
        modal
        open={open}
        onOpenChange={setOpen}
        snapPoints={[95]}
        dismissOnSnapToBottom={false}
        dismissOnOverlayPress={false}
        animation="medium">
        <Sheet.Overlay animation="lazy" enterStyle={{ opacity: 0 }} exitStyle={{ opacity: 0 }} backgroundColor="rgba(0,0,0,0.4)" />
        <Sheet.Handle />
        <Sheet.Frame bg="#ffffff" borderTopLeftRadius={24} borderTopRightRadius={24}>
          <XStack alignItems="center" justifyContent="space-between" padding="$5">
            <YStack>
              <Text fontWeight="bold">選擇優惠券</Text>
            </YStack>
            <Button
              p={4}
              chromeless
              onPress={() => setOpen(false)}
              backgroundColor={colors.primaryActive}
              pressStyle={{ backgroundColor: colors.primaryActive, borderColor: colors.primaryActive, borderWidth: 1 }}
              hoverStyle={{ backgroundColor: colors.primaryActive, borderColor: colors.primaryActive, borderWidth: 1 }}>
              <RiCloseFill size={22} />
            </Button>
          </XStack>

          <ScrollView showsVerticalScrollIndicator={false} background={colors.bg}>
            <YStack gap={3} p="$3">
              {loading ? (
                <Spinner size="large" color={colors.primary} mt="$6" alignSelf="center" />
              ) : coupons.length === 0 ? (
                <Text color={colors.gray} fontSize={14} textAlign="center" mt="$6">
                  目前沒有可用的優惠券
                </Text>
              ) : (
                coupons.map((coupon) => {
                  const isSelected = selectedCoupon?.id === coupon.id;
                  const notYet = isBeforeUseStart(coupon.useStart);
                  return (
                    <button
                      key={coupon.id}
                      type="button"
                      onClick={() => handleSelect(coupon)}
                      className={`flex items-center gap-3 px-3 py-3 mt-3 rounded-xl border w-full text-left transition-colors ${
                        notYet
                          ? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
                          : isSelected
                          ? "border-blue-200 bg-blue-50 active:bg-blue-100"
                          : "border-gray-200 bg-gray-50 active:bg-gray-100"
                      }`}>
                      <RiCoupon3Line className={`w-5 h-5 flex-shrink-0 ${isSelected && !notYet ? "text-blue-500" : "text-gray-400"}`} />
                      <div>
                        <div className={`font-semibold text-sm leading-snug ${isSelected && !notYet ? "text-gray-800" : "text-gray-500"}`}>
                          {coupon.title}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {notYet
                            ? `${coupon.useStart ? formatDate(coupon.useStart) : ""} 起可用・滿 $${coupon.minSpend} 可用`
                            : `折抵 $${coupon.amount}・滿 $${coupon.minSpend} 可用・${couponExpiryLabel(coupon.expiry)}`}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </YStack>
          </ScrollView>
        </Sheet.Frame>
      </Sheet>
    </>
  );
}
